// Confirm-to-commit action cards for PA proposed tools (PA-2a).

import { useCallback, useEffect, useState } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { listOpsNotificationMembers } from "@/lib/opsPaperworkClientPartA";
import type { PaProposedTool, PaToolId } from "@/lib/personalAssistant/paToolCatalog";
import { executePaTool } from "@/lib/personalAssistant/paToolExecuteClient";
import { PaActionCardDraftFields } from "./PaActionCardDraftFields";
import { PaActionCardObligationFields } from "./PaActionCardObligationFields";

export type PaActionCardProps = {
  proposal: PaProposedTool;
  assistantMessageId: string;
  getToken: () => Promise<string | null>;
  organizationId: string | null;
  assistMode: "personal_assistant" | "default";
  impersonationActive?: boolean;
  disabled?: boolean;
  onResolved: (proposalId: string, status: PaProposedTool["status"]) => void;
  onNavigate?: (mode: PrimaryMode, highlightTarget?: string) => void;
  onEditInForm?: (toolId: PaToolId) => void;
};

type MemberOption = { user_id: string; name: string };

function toLocalDatetimeValue(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalDatetimeValue(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return undefined;
  return new Date(ms).toISOString();
}

export function PaActionCard({
  proposal,
  assistantMessageId,
  getToken,
  organizationId,
  assistMode,
  impersonationActive = false,
  disabled = false,
  onResolved,
  onNavigate,
  onEditInForm,
}: PaActionCardProps) {
  const [args, setArgs] = useState<Record<string, unknown>>({ ...proposal.arguments });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [members, setMembers] = useState<MemberOption[]>([]);

  const isPending = proposal.status === "pending";

  useEffect(() => {
    if (!isPending) return;
    if (
      proposal.toolId !== "notification_send" &&
      proposal.toolId !== "task_create" &&
      proposal.toolId !== "notification_schedule"
    ) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await listOpsNotificationMembers();
      if (!res.ok || cancelled) return;
      const rows = (res.data.members ?? []) as Array<{ user_id?: string; name?: string }>;
      setMembers(
        rows
          .map((m) => ({
            user_id: String(m.user_id ?? ""),
            name: String(m.name ?? m.user_id ?? "Member"),
          }))
          .filter((m) => m.user_id),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [isPending, proposal.toolId]);

  const runAction = useCallback(
    async (action: "confirm" | "cancel") => {
      setBusy(true);
      setError(null);
      try {
        const token = await getToken();
        if (!token) throw new Error("Sign in required");
        const result = await executePaTool({
          token,
          organizationId,
          assistMode,
          impersonationActive,
          action,
          proposalId: proposal.proposalId,
          toolId: proposal.toolId,
          arguments: args,
          assistantMessageId,
        });
        if (!result.ok) {
          throw new Error(result.error ?? "Action failed");
        }
        if (action === "cancel") {
          onResolved(proposal.proposalId, "cancelled");
          return;
        }
        const nav = result.result?.navigateTo;
        if (nav?.mode && onNavigate) {
          onNavigate(nav.mode as PrimaryMode, nav.highlightTarget);
        }
        onResolved(proposal.proposalId, "executed");
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [
      args,
      assistantMessageId,
      assistMode,
      impersonationActive,
      onNavigate,
      onResolved,
      organizationId,
      proposal.proposalId,
      proposal.toolId,
      getToken,
    ],
  );

  if (!isPending && proposal.status !== "failed") {
    const label =
      proposal.status === "executed"
        ? "Confirmed"
        : proposal.status === "cancelled"
          ? "Cancelled"
          : proposal.status;
    return (
      <div className="mt-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] text-gray-600 dark:border-zinc-700 dark:bg-zinc-900/50 dark:text-zinc-400">
        {proposal.summary} — <span className="font-medium">{label}</span>
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-1.5 rounded-lg border border-amber-300/80 bg-amber-50/90 p-2 dark:border-amber-700/50 dark:bg-amber-950/30">
      <p className="text-[11px] font-medium text-amber-950 dark:text-amber-100">{proposal.summary}</p>

      {proposal.toolId === "note_append" ? (
        <label className="block text-[10px] text-amber-900 dark:text-amber-200">
          Note
          <textarea
            value={String(args.body ?? "")}
            onChange={(e) => setArgs((prev) => ({ ...prev, body: e.target.value }))}
            disabled={disabled || busy}
            rows={2}
            className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
          />
        </label>
      ) : null}

      {proposal.toolId === "notification_schedule" ? (
        <div className="space-y-1">
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Title
            <input
              value={String(args.title ?? "")}
              onChange={(e) => setArgs((prev) => ({ ...prev, title: e.target.value }))}
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Remind at
            <input
              type="datetime-local"
              value={toLocalDatetimeValue(String(args.due_at ?? ""))}
              onChange={(e) => {
                const iso = fromLocalDatetimeValue(e.target.value);
                setArgs((prev) => ({ ...prev, due_at: iso ?? prev.due_at }));
              }}
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            For member
            <select
              value={String(args.assignee_user_id ?? "")}
              onChange={(e) =>
                setArgs((prev) => ({
                  ...prev,
                  assignee_user_id: e.target.value || undefined,
                }))
              }
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            >
              <option value="">Me (default)</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {proposal.toolId === "notification_send" ? (
        <div className="space-y-1">
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Title
            <input
              value={String(args.title ?? "")}
              onChange={(e) => setArgs((prev) => ({ ...prev, title: e.target.value }))}
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Message
            <textarea
              value={String(args.body ?? "")}
              onChange={(e) => setArgs((prev) => ({ ...prev, body: e.target.value }))}
              disabled={disabled || busy}
              rows={2}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Recipients
            <select
              multiple
              value={(args.recipient_user_ids as string[] | undefined) ?? []}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                setArgs((prev) => ({ ...prev, recipient_user_ids: selected }));
              }}
              disabled={disabled || busy}
              className="mt-0.5 h-16 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            >
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {proposal.toolId === "checklist_nudge" ? (
        <div className="space-y-1">
          <p className="text-[10px] text-amber-800 dark:text-amber-200">
            Broadcasts to all active org members (same as Location Tools checklist nudge).
          </p>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Title
            <input
              value={String(args.title ?? "")}
              onChange={(e) => setArgs((prev) => ({ ...prev, title: e.target.value }))}
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Message (optional)
            <textarea
              value={String(args.body ?? "")}
              onChange={(e) => setArgs((prev) => ({ ...prev, body: e.target.value }))}
              disabled={disabled || busy}
              rows={2}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
        </div>
      ) : null}

      {proposal.toolId === "task_create" ? (
        <div className="space-y-1">
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Task title
            <input
              value={String(args.title ?? "")}
              onChange={(e) => setArgs((prev) => ({ ...prev, title: e.target.value }))}
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Assignee
            <select
              value={String(args.assignee_user_id ?? "")}
              onChange={(e) => setArgs((prev) => ({ ...prev, assignee_user_id: e.target.value }))}
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            >
              <option value="">Select member…</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-[10px] text-amber-900 dark:text-amber-200">
            Due (optional)
            <input
              type="datetime-local"
              value={toLocalDatetimeValue(String(args.due_at ?? ""))}
              onChange={(e) => {
                const iso = fromLocalDatetimeValue(e.target.value);
                setArgs((prev) => ({ ...prev, due_at: iso }));
              }}
              disabled={disabled || busy}
              className="mt-0.5 w-full rounded border border-amber-200 bg-white px-1.5 py-1 text-xs dark:border-amber-800 dark:bg-zinc-900"
            />
          </label>
        </div>
      ) : null}

        {proposal.toolId === "deep_link" ? (
        <p className="text-[10px] text-amber-900 dark:text-amber-200">
          Navigate to <span className="font-medium">{String(args.target_mode)}</span>
        </p>
      ) : null}

      {proposal.toolId === "obligation_lookup" ? (
        <p className="text-[10px] text-amber-900 dark:text-amber-200">
          Search Ops obligations
          {args.location_query ? ` at ${String(args.location_query)}` : ""}
          {args.kind ? ` (${String(args.kind)})` : ""}.
        </p>
      ) : null}

      {proposal.toolId === "obligation_create" ? (
        <PaActionCardObligationFields args={args} setArgs={setArgs} disabled={disabled || busy} />
      ) : null}

      {proposal.toolId === "service_request_draft" || proposal.toolId === "lead_draft" ? (
        <PaActionCardDraftFields
          toolId={proposal.toolId}
          args={args}
          setArgs={setArgs}
          disabled={disabled || busy}
        />
      ) : null}

      {error ? (
        <p className="text-[10px] text-red-700 dark:text-red-300" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => void runAction("confirm")}
          className="rounded bg-amber-700 px-2 py-1 text-[10px] font-semibold text-white hover:bg-amber-800 disabled:opacity-50"
        >
          {busy ? "Working…" : "Confirm"}
        </button>
        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => void runAction("cancel")}
          className="rounded border border-amber-400 px-2 py-1 text-[10px] font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:text-amber-100 dark:hover:bg-amber-900/40"
        >
          Cancel
        </button>
        {proposal.toolId !== "deep_link" &&
        proposal.toolId !== "obligation_lookup" &&
        proposal.toolId !== "obligation_create" &&
        onEditInForm ? (
          <button
            type="button"
            disabled={disabled || busy}
            onClick={() => onEditInForm(proposal.toolId)}
            className="rounded border border-gray-300 px-2 py-1 text-[10px] text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-300"
          >
            Edit in form
          </button>
        ) : null}
      </div>
    </div>
  );
}
